import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Textarea } from '@/components/ui/textarea';
import { ProfileAvatar } from '@/components/ProfileAvatar';
import { cn } from '@/lib/utils';

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange?: (mentions: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

interface UserSuggestion {
  id: string;
  username: string;
  avatar_url?: string;
}

export const MentionTextarea = ({
  value,
  onChange,
  onMentionsChange,
  placeholder,
  disabled,
  className,
}: MentionTextareaProps) => {
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (mentionSearch) {
      searchUsers(mentionSearch);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [mentionSearch]);

  const searchUsers = async (query: string) => {
    const { data, error } = await supabase
      .from('public_profile_data')
      .select('id, username, avatar_url')
      .ilike('username', `${query}%`)
      .limit(5);

    if (!error && data) {
      setSuggestions(data as UserSuggestion[]);
      setShowSuggestions(data.length > 0);
      setSelectedIndex(0);
    }
  };

  const handleTextChange = (newValue: string) => {
    onChange(newValue);

    // Extract mentions for notification purposes
    const mentions = newValue.match(/@(\w+)/g)?.map(m => m.slice(1)) || [];
    onMentionsChange?.(mentions);

    // Check if user is typing a mention
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtSymbol + 1);
      // Only show suggestions if there's no space after @
      if (!textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt);
      } else {
        setMentionSearch('');
      }
    } else {
      setMentionSearch('');
    }
  };

  const insertMention = (username: string) => {
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursorPos);
    const textAfterCursor = value.slice(cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    const newValue =
      value.slice(0, lastAtSymbol) + `@${username} ` + textAfterCursor;

    onChange(newValue);
    setShowSuggestions(false);
    setMentionSearch('');
    
    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
      const newCursorPos = lastAtSymbol + username.length + 2;
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        insertMention(suggestions[selectedIndex].username);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setMentionSearch('');
      }
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => handleTextChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
          {suggestions.map((user, index) => (
            <button
              key={user.id}
              onClick={() => insertMention(user.username)}
              className={cn(
                "w-full px-4 py-2 text-left text-gray-900 dark:text-foreground hover:bg-gray-100 dark:hover:bg-accent flex items-center gap-2 transition-colors",
                index === selectedIndex && "bg-gray-100 dark:bg-accent"
              )}
            >
              <ProfileAvatar
                src={user.avatar_url}
                alt={`${user.username}'s avatar`}
                fallback={user.username[0]?.toUpperCase() || 'U'}
                className="w-6 h-6"
              />
              <span className="text-sm">@{user.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
